// Admin Teams page (/admin/teams): toolbar, compact team table, roster side panel.
// Pieces live in ./teams/ (table, panel, create/edit form, bulk import, toasts); parsers in
// ../utils/teamImport.js. Server: routes/teams.js (list has ?q= search, rosters, cached FACEIT).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { ConfirmModal } from './AlertModal';
import Pagination from './Pagination';
import { IconPlus, IconUsers } from './Icons';
import { cacheGet, cacheSet, cacheDrop } from '../utils/clientCache';
import TeamsTable from './teams/TeamsTable';
import TeamPanel from './teams/TeamPanel';
import TeamFormModal from './teams/TeamFormModal';
import BulkImportModal from './teams/BulkImportModal';
import { useToasts, Toasts } from './teams/Toasts';
import './teams/teams.css';

const PAGE_SIZES = [10, 50, 100];
const cacheKey = (view, page, size) => `teams:list:${view}:${page}:${size}`;

function Teams() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuper = user && (user.role === 'super_admin' || user.role === 'admin');

  const [view, setView] = useState('active');           // 'active' | 'archived'
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');                        // debounced search sent to the server
  const cached = cacheGet(cacheKey('active', 1, 10));
  const [teams, setTeams] = useState(cached ? cached.data : []);
  const [pagination, setPagination] = useState(cached ? cached.pagination : { total: 0, totalPages: 1 });
  const [status, setStatus] = useState(cached ? 'ready' : 'loading');
  const [loadError, setLoadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [panelTeam, setPanelTeam] = useState(null);
  const [form, setForm] = useState({ open: false, team: null });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [confirmState, setConfirmState] = useState({ show: false, message: '', onConfirm: null, confirmText: '' });
  const [working, setWorking] = useState(false);          // a bulk action is running
  const { toasts, push: toast, dismiss } = useToasts();
  const reqRef = useRef(null);

  // debounce the search box (one request after typing stops)
  useEffect(() => {
    const id = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(id);
  }, [qInput]);

  // a new view / page / size / search = a new set of rows → page 1 for filters, selection cleared
  useEffect(() => { setPage(1); }, [view, pageSize, q]);
  useEffect(() => { setSelected(new Set()); }, [view, page, pageSize, q]);

  // ONE fetch for the current view/page/size/search; the previous request is cancelled so a slow
  // earlier answer can never overwrite a newer one. Cached pages show at once (refreshed after).
  useEffect(() => {
    if (reqRef.current) reqRef.current.abort();
    const ctl = new AbortController();
    reqRef.current = ctl;
    const key = cacheKey(view, page, pageSize);
    const hit = !q && cacheGet(key);
    if (hit) { setTeams(hit.data); setPagination(hit.pagination); setStatus('ready'); } else setStatus('loading');
    axios.get('/api/teams', { params: { page, limit: pageSize, archived: view === 'archived' ? 1 : undefined, q: q || undefined }, signal: ctl.signal })
      .then(res => {
        setTeams(res.data.data || []);
        setPagination(res.data.pagination || { total: 0, totalPages: 1 });
        setStatus('ready');
        setLoadError('');
        if (!q) cacheSet(key, { data: res.data.data || [], pagination: res.data.pagination });
        // the last page got emptied (e.g. deletes) → step back
        const tp = (res.data.pagination && res.data.pagination.totalPages) || 1;
        if (page > tp && tp >= 1) setPage(tp);
      })
      .catch(err => {
        if (axios.isCancel(err) || err.name === 'CanceledError') return;
        setStatus('error');
        setLoadError(err.response?.data?.error || t('teams.loadFailed', 'Could not load teams.'));
      });
    return () => ctl.abort();
  }, [view, page, pageSize, q, reloadKey, t]);

  const reload = useCallback(() => { cacheDrop('teams:list'); setReloadKey(k => k + 1); }, []);

  // a change made in the panel / form → patch that row in place (no refetch)
  const patchRow = useCallback((next) => {
    cacheDrop('teams:list');
    setTeams(list => list.map(x => (x.id === next.id ? { ...x, ...next } : x)));
    setPanelTeam(cur => (cur && cur.id === next.id ? { ...cur, ...next } : cur));
  }, []);

  const confirm = useCallback((message, onConfirm, confirmText) => setConfirmState({ show: true, message, onConfirm, confirmText: confirmText || t('common.confirm', 'Confirm') }), [t]);

  const reasonText = useCallback((r) => ({
    team_in_match: t('teams.reasonInMatch', 'in a live or pending match'),
    not_owner: t('teams.reasonNotOwner', 'not your team'),
    not_found: t('teams.reasonNotFound', 'no longer exists'),
  }[r] || r), [t]);
  const nameOf = useCallback((id) => (teams.find(x => x.id === id) || {}).name || `#${id}`, [teams]);

  // ── single-team actions (⋯ menu / panel) ──
  const archiveTeams = useCallback(async (ids, archive) => {
    setWorking(true);
    try {
      const res = await axios.post('/api/teams/archive', { ids, archived: archive });
      const skipped = res.data.skipped || [];
      if (res.data.updated) {
        toast(archive ? t('teams.archiveSuccess', 'Archived {{count}} teams. They are hidden from the list and match creation.', { count: res.data.updated })
          : t('teams.unarchiveSuccess', 'Restored {{count}} teams.', { count: res.data.updated }));
      }
      if (skipped.length) toast(t('teams.skippedList', 'Not changed:\n{{list}}', { list: skipped.map(s => `${nameOf(s.id)} - ${reasonText(s.reason)}`).join('\n') }), 'warning');
      setSelected(new Set(skipped.map(s => s.id)));
      if (panelTeam && ids.includes(panelTeam.id) && !skipped.some(s => s.id === panelTeam.id)) setPanelTeam(null);
      reload();
    } catch (err) {
      toast(err.response?.data?.error || t('teams.archiveFailed', 'Failed to archive teams'), 'error');
    } finally { setWorking(false); }
  }, [nameOf, panelTeam, reasonText, reload, t, toast]);

  const deleteTeams = useCallback(async (list) => {
    setWorking(true);
    const failed = [];
    let ok = 0;
    for (const team of list) {
      try { await axios.delete(`/api/teams/${team.id}`); ok++; }
      catch (err) {
        const d = err.response?.data || {};
        failed.push({ team, reason: d.code === 'team_in_match' ? t('teams.reasonInMatchId', 'in match #{{id}}', { id: d.match_id }) : (d.error || t('teams.deleteFailed', 'Failed to delete team')) });
      }
    }
    if (ok) toast(t('teams.deletedCount', 'Deleted {{count}} teams', { count: ok }));
    if (failed.length) toast(t('teams.notDeletedList', 'Not deleted:\n{{list}}', { list: failed.map(f => `${f.team.name} - ${f.reason}`).join('\n') }), 'error');
    setSelected(new Set(failed.map(f => f.team.id)));
    if (panelTeam && list.some(x => x.id === panelTeam.id) && !failed.some(f => f.team.id === panelTeam.id)) setPanelTeam(null);
    setWorking(false);
    reload();
  }, [panelTeam, reload, t, toast]);

  const onAction = useCallback((kind, team) => {
    if (kind === 'roster') setPanelTeam(team);
    else if (kind === 'edit') setForm({ open: true, team });
    else if (kind === 'archive') archiveTeams([team.id], view !== 'archived');
    else if (kind === 'delete') {
      confirm(t('teams.deleteConfirmOne', 'Delete {{name}} and its roster for good? Past matches keep the team name. Archive hides a team instead and can be undone.', { name: team.name }),
        () => deleteTeams([team]), t('common.delete', 'Delete'));
    }
  }, [archiveTeams, confirm, deleteTeams, t, view]);

  const toggle = useCallback((id) => setSelected(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; }), []);
  const toggleAll = useCallback((on) => setSelected(on ? new Set(teams.map(x => x.id)) : new Set()), [teams]);
  const openPanel = useCallback((team) => setPanelTeam(team), []);
  const closePanel = useCallback(() => setPanelTeam(null), []);
  const selectedTeams = teams.filter(x => selected.has(x.id));

  const onSaved = (team, editing) => {
    setForm({ open: false, team: null });
    if (editing) { patchRow(team); toast(t('teams.updated', 'Team updated')); }
    else { toast(t('teams.created', 'Created {{name}}', { name: team.name })); reload(); setPanelTeam({ ...team, players: [], avatar_v: 0 }); }
  };

  return (
    <div className="tm-page">
      <div className="tm-toolbar">
        <h1 className="tm-title">{t('teams.title', 'Teams')}</h1>
        <span className="tm-count">{t('teams.count', '{{count}} teams', { count: pagination.total || 0 })}</span>
        <span className="tm-tabs" role="tablist" aria-label={t('teams.view', 'View')}>
          <button type="button" role="tab" aria-selected={view === 'active'} className={view === 'active' ? 'on' : ''} onClick={() => setView('active')}>{t('teams.viewActive', 'Active')}</button>
          <button type="button" role="tab" aria-selected={view === 'archived'} className={view === 'archived' ? 'on' : ''} onClick={() => setView('archived')}>{t('teams.viewArchived', 'Archived')}</button>
        </span>
        <div className="tm-search">
          <input type="search" value={qInput} onChange={e => setQInput(e.target.value)} placeholder={t('teams.searchPlaceholder2', 'Search team, tag, coach or player…')} aria-label={t('teams.search', 'Search teams')} />
          {qInput && <button type="button" className="tm-clear" onClick={() => setQInput('')} aria-label={t('teams.clearSearch', 'Clear search')}>×</button>}
        </div>
        <span className="tm-spacer" />
        <span className="tm-seg" aria-label={t('teams.perPage', 'Teams per page')}>
          {PAGE_SIZES.map(n => <button key={n} type="button" className={pageSize === n ? 'on' : ''} onClick={() => setPageSize(n)}>{n}</button>)}
        </span>
        <button type="button" className="tm-btn" onClick={() => setBulkOpen(true)}><IconUsers size={14} /> {t('teams.bulkAdd', 'Bulk Add')}</button>
        <button type="button" className="tm-btn primary" onClick={() => setForm({ open: true, team: null })}><IconPlus size={14} /> {t('teams.createTeam', 'Create Team')}</button>
      </div>

      {selectedTeams.length > 0 && (
        <div className="tm-selbar">
          <span><strong>{selectedTeams.length}</strong> {t('teams.selectedShort', 'selected')}</span>
          <button type="button" className="tm-btn small" onClick={() => setSelected(new Set())}>{t('teams.clearSelection', 'Clear')}</button>
          <span className="tm-spacer" />
          <button type="button" className="tm-btn small warn" disabled={working} onClick={() => archiveTeams(selectedTeams.map(x => x.id), view !== 'archived')}>
            {working && <span className="tm-spin" />}
            {view === 'archived' ? t('teams.unarchiveSelected', 'Restore ({{count}})', { count: selectedTeams.length }) : t('teams.archiveSelected', 'Archive ({{count}})', { count: selectedTeams.length })}
          </button>
          <button type="button" className="tm-btn small danger" disabled={working}
            onClick={() => confirm(t('teams.bulkDeleteConfirm2', 'Delete {{count}} teams and their rosters for good? Teams in a live or pending match are skipped.', { count: selectedTeams.length }), () => deleteTeams(selectedTeams), t('common.delete', 'Delete'))}>
            {t('teams.deleteSelected', 'Delete Selected ({{count}})', { count: selectedTeams.length })}
          </button>
        </div>
      )}

      <TeamsTable teams={teams} status={status} error={loadError} q={q} view={view} selected={selected} activeId={panelTeam && panelTeam.id} showOwner={isSuper}
        onToggle={toggle} onToggleAll={toggleAll} onOpen={openPanel} onAction={onAction} onRetry={reload}
        onClearSearch={() => setQInput('')} onCreate={() => setForm({ open: true, team: null })} />

      <Pagination page={page} totalPages={pagination.totalPages || 1} onPageChange={setPage} />

      <AnimatePresence>
        {panelTeam && (
          <TeamPanel key={panelTeam.id} team={panelTeam} onClose={closePanel} onTeamChanged={patchRow}
            onEdit={(team) => setForm({ open: true, team })} toast={toast} confirm={confirm} escapeBlocked={confirmState.show || form.open} />
        )}
      </AnimatePresence>

      <TeamFormModal isOpen={form.open} team={form.team} onClose={() => setForm({ open: false, team: null })} onSaved={onSaved} onError={(m) => toast(m, 'error')} />
      <BulkImportModal isOpen={bulkOpen} onClose={() => setBulkOpen(false)} onImported={() => reload()} />
      <ConfirmModal show={confirmState.show} message={confirmState.message} confirmText={confirmState.confirmText}
        onConfirm={() => { const fn = confirmState.onConfirm; setConfirmState(s => ({ ...s, show: false })); if (fn) fn(); }}
        onClose={() => setConfirmState(s => ({ ...s, show: false }))} />
      <Toasts toasts={toasts} dismiss={dismiss} />
    </div>
  );
}

export default Teams;
